import React, { useState, useEffect } from 'react';
import { Listing } from '../types';
import { Button, Flex, Input, Text, Card, Select } from './base-components';
import { useUpdateListing } from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { trackEvent } from '../services/analytics';

interface Props {
  listing: Listing | null;
  open: boolean;
  onClose: () => void;
}

const formatTimeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

export const EditListingModal: React.FC<Props> = ({ listing, open, onClose }) => {
  const { showSuccess, showError } = useToast();
  const updateMutation = useUpdateListing();

  const [pricePerTicket, setPricePerTicket] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [listingNotes, setListingNotes] = useState('');
  const [inHandDate, setInHandDate] = useState('');
  const [section, setSection] = useState('');
  const [row, setRow] = useState('');

  useEffect(() => {
    if (listing) {
      setPricePerTicket(listing.price_per_ticket);
      setStatus(listing.status === 'ACTIVE' || listing.status === 'INACTIVE' ? listing.status : 'ACTIVE');
      setListingNotes(listing.listing_notes || '');
      setInHandDate(listing.in_hand_date ? listing.in_hand_date.slice(0, 10) : '');
      setSection(listing.section || '');
      setRow(listing.row || '');
    }
  }, [listing]);

  useEffect(() => {
    if (!open) {
      setPricePerTicket('');
      setStatus('ACTIVE');
      setListingNotes('');
      setInHandDate('');
      setSection('');
      setRow('');
    }
  }, [open]);

  if (!open || !listing) return null;

  const hasCVMirror = !!listing.cv_listing_id;
  const totalPrice = (parseFloat(pricePerTicket || '0') * listing.quantity).toFixed(2);

  const handleSave = async () => {
    if (!pricePerTicket || isNaN(parseFloat(pricePerTicket))) {
      showError('Please enter a valid price');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: listing.id,
        data: {
          price_per_ticket: pricePerTicket,
          status,
          listing_notes: listingNotes || null,
          in_hand_date: inHandDate || null,
          section: section || null,
          row: row || null,
        },
      });
      showSuccess('Listing updated');
      trackEvent('ft_listing_updated', { listingId: String(listing.id), price: pricePerTicket, status });
      onClose();
    } catch (err: any) {
      showError(err?.message || 'Failed to update listing');
    }
  };

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      backdrop
      zIndex={1000}
      animate="fadeIn"
      onClick={onClose}
    >
      <Card
        fullWidth
        maxWidth="md"
        shadow
        animate="slideUp"
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        <Text variant="heading" mb={1}>Edit FT Listing</Text>
        {listing.event && (
          <Text variant="muted" mb={3}>
            {listing.event.name}
            {listing.event.venue && ` · ${listing.event.venue.name}`}
          </Text>
        )}

        {/* CV mirror indicator */}
        {hasCVMirror && (
          <div className="mb-4 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <p className="text-sm text-violet-300 font-medium">⚡ Also updates your CrowdVolt listing</p>
            {listing.cv_last_mirrored_at && (
              <Text variant="muted" size="xs" mt={1}>
                Last mirrored {formatTimeAgo(listing.cv_last_mirrored_at)} at ${listing.cv_last_mirrored_price}
              </Text>
            )}
          </div>
        )}

        <Flex direction="col" gap={3}>
          {/* Price per ticket */}
          <div>
            <Input
              type="number"
              label="Price Per Ticket ($)"
              placeholder="0.00"
              value={pricePerTicket}
              onChange={(e) => setPricePerTicket(e.target.value)}
            />
            {listing.quantity > 1 && (
              <Text variant="muted" size="xs" mt={1}>
                Total: ${totalPrice} ({listing.quantity} tickets)
              </Text>
            )}
          </div>

          {/* Status */}
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'INACTIVE')}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'INACTIVE', label: 'Inactive' },
            ]}
          />

          {/* Section / Row */}
          <Flex gap={3}>
            <Flex direction="col" grow>
              <Input
                type="text"
                label="Section"
                placeholder="Floor"
                value={section}
                onChange={(e) => setSection(e.target.value)}
              />
            </Flex>
            <Flex direction="col" grow>
              <Input
                type="text"
                label="Row"
                placeholder="A"
                value={row}
                onChange={(e) => setRow(e.target.value)}
              />
            </Flex>
          </Flex>

          {/* In-hand date */}
          <Input
            type="date"
            label="In-Hand Date"
            value={inHandDate}
            onChange={(e) => setInHandDate(e.target.value)}
          />

          {/* Notes */}
          <div>
            <label className="block text-sm text-white/60 mb-1">Listing Notes</label>
            <textarea
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#3b82f6]/50 resize-none"
              rows={3}
              placeholder="Any notes for the buyer…"
              value={listingNotes}
              onChange={(e) => setListingNotes(e.target.value)}
            />
          </div>
        </Flex>

        <Flex justify="end" gap={2} mt={4}>
          <Button variant="secondary" onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </Flex>
      </Card>
    </Flex>
  );
};
